import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import "../assets/styles/approve_goods_receipt_list.scss";

const FOLLOW_UP_OPTIONS = [
  { value: "", label: "Select action..." },
  { value: "RETURN_TO_SUPPLIER", label: "Return to Supplier" },
  { value: "REPLACEMENT_REQUIRED", label: "Replacement Required" },
  { value: "CREDIT_NOTE", label: "Issue Credit Note" },
  { value: "INVESTIGATE", label: "Investigate Further" },
  { value: "ACCEPT_WITH_DISCOUNT", label: "Accept with Discount" },
  { value: "OTHER", label: "Other" },
];

const initialForm = () => ({
  decision: "",
  approverName: "",
  designation: "",
  approvalDate: new Date().toISOString().split("T")[0],
  approvalNotes: "",
  followUpAction: "",
  rejectionReason: "",
});

/**
 * Same fields and layout as `/approve-goods-receipt-list` GRN acceptance modal (`agrl-accept-*`).
 * Submit calls parent with validated form; parent runs GRN APIs + logistics `accept-grn` when applicable.
 */
export default function LogisticsGrnAgrlAcceptModal({ open, grn, onClose, onSubmitDecision, submitting }) {
  const [acceptanceForm, setAcceptanceForm] = useState(initialForm);

  useEffect(() => {
    if (open) setAcceptanceForm(initialForm());
  }, [open]);

  const handleFormChange = useCallback((field, value) => {
    setAcceptanceForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  if (!open || !grn || typeof grn !== "object") return null;

  const grnNumber = grn.grn_number != null && String(grn.grn_number).trim() !== "" ? String(grn.grn_number) : "N/A";
  const poNumber =
    grn.po_number != null && String(grn.po_number).trim() !== ""
      ? String(grn.po_number)
      : grn.purchase_order_id != null && Number.isFinite(Number(grn.purchase_order_id)) && Number(grn.purchase_order_id) > 0
        ? `PO-${grn.purchase_order_id}`
        : "N/A";
  const totalAccepted =
    grn.total_qty_accepted != null && String(grn.total_qty_accepted).trim() !== ""
      ? Number(grn.total_qty_accepted)
      : 0;
  const totalRejected =
    grn.total_qty_rejected != null && String(grn.total_qty_rejected).trim() !== ""
      ? Number(grn.total_qty_rejected)
      : 0;
  const supplier =
    grn.supplier_name != null && String(grn.supplier_name).trim() !== "" ? String(grn.supplier_name) : "N/A";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptanceForm.decision || !acceptanceForm.approverName || !acceptanceForm.designation) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (acceptanceForm.decision === "APPROVE" && !String(acceptanceForm.approvalNotes || "").trim()) {
      toast.error("Please enter approval notes");
      return;
    }
    if (acceptanceForm.decision === "PARTIALLY_ACCEPT" && !acceptanceForm.followUpAction) {
      toast.error("Please select a follow-up action");
      return;
    }
    if (acceptanceForm.decision === "REJECT") {
      if (!String(acceptanceForm.rejectionReason || "").trim()) {
        toast.error("Please enter a rejection reason");
        return;
      }
      if (!acceptanceForm.followUpAction) {
        toast.error("Please select a follow-up action");
        return;
      }
    }
    if (typeof onSubmitDecision !== "function") return;
    await onSubmitDecision({ ...acceptanceForm });
  };

  return (
    <div className="approve-goods-receipt-list agrl-modal-host-only">
      <div className="agrl-accept-modal-overlay" onClick={submitting ? undefined : onClose} role="presentation">
        <div
          className="agrl-accept-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="logistics-agrl-accept-title"
          aria-describedby="logistics-agrl-accept-description"
          tabIndex={-1}
          onClick={(ev) => ev.stopPropagation()}
        >
          <button
            type="button"
            className="agrl-accept-modal-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close acceptance modal"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>

          <div className="agrl-accept-modal-header">
            <div>
              <h2 id="logistics-agrl-accept-title" className="agrl-accept-modal-title">
                GRN Acceptance Decision — {grnNumber}
              </h2>
              <p id="logistics-agrl-accept-description" className="agrl-accept-modal-subtitle">
                Review and provide your acceptance decision for this GRN (same steps as GRN Acceptance &amp; Approval).
                After approve or partially approve, the shipment workflow is updated for the buyer.
              </p>
            </div>
          </div>

          <form className="agrl-accept-modal-content" onSubmit={handleSubmit}>
            <div className="agrl-accept-summary">
              <div className="agrl-accept-summary-grid">
                <div>
                  <label className="agrl-accept-summary-label">PO Number</label>
                  <p className="agrl-accept-summary-value agrl-value-mono">{poNumber}</p>
                </div>
                <div>
                  <label className="agrl-accept-summary-label">Total Accepted</label>
                  <p className="agrl-accept-summary-value agrl-text-green">{totalAccepted}</p>
                </div>
                <div>
                  <label className="agrl-accept-summary-label">Total Rejected</label>
                  <p className="agrl-accept-summary-value agrl-text-red">{totalRejected}</p>
                </div>
                <div>
                  <label className="agrl-accept-summary-label">Supplier</label>
                  <p className="agrl-accept-summary-value agrl-text-truncate">{supplier}</p>
                </div>
              </div>
            </div>

            <div className="agrl-accept-decision-section">
              <label className="agrl-accept-decision-label">
                Acceptance Decision <span className="agrl-required">*</span>
              </label>
              <div className="agrl-accept-radio-group">
                <div
                  className={`agrl-accept-radio-option ${acceptanceForm.decision === "APPROVE" ? "agrl-radio-selected" : ""}`}
                  onClick={() => handleFormChange("decision", "APPROVE")}
                  role="presentation"
                >
                  <input
                    type="radio"
                    name="logisticsAgrlAcceptanceDecision"
                    id="logistics-agrl-approve"
                    value="APPROVE"
                    checked={acceptanceForm.decision === "APPROVE"}
                    onChange={() => handleFormChange("decision", "APPROVE")}
                    className="agrl-radio-input"
                  />
                  <label htmlFor="logistics-agrl-approve" className="agrl-radio-label">
                    <div className="agrl-radio-content">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="agrl-radio-icon agrl-icon-green"
                      >
                        <path d="M21.801 10A10 10 0 1 1 17 3.335" />
                        <path d="m9 11 3 3L22 4" />
                      </svg>
                      <div>
                        <p className="agrl-radio-title">Approve GRN</p>
                        <p className="agrl-radio-description">Accept all received items and update inventory</p>
                      </div>
                    </div>
                  </label>
                </div>

                <div
                  className={`agrl-accept-radio-option ${acceptanceForm.decision === "PARTIALLY_ACCEPT" ? "agrl-radio-selected" : ""}`}
                  onClick={() => handleFormChange("decision", "PARTIALLY_ACCEPT")}
                  role="presentation"
                >
                  <input
                    type="radio"
                    name="logisticsAgrlAcceptanceDecision"
                    id="logistics-agrl-partial"
                    value="PARTIALLY_ACCEPT"
                    checked={acceptanceForm.decision === "PARTIALLY_ACCEPT"}
                    onChange={() => handleFormChange("decision", "PARTIALLY_ACCEPT")}
                    className="agrl-radio-input"
                  />
                  <label htmlFor="logistics-agrl-partial" className="agrl-radio-label">
                    <div className="agrl-radio-content">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="agrl-radio-icon agrl-icon-orange"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" x2="12" y1="8" y2="12" />
                        <line x1="12" x2="12.01" y1="16" y2="16" />
                      </svg>
                      <div>
                        <p className="agrl-radio-title">Partially Accept</p>
                        <p className="agrl-radio-description">Accept some items and reject others</p>
                      </div>
                    </div>
                  </label>
                </div>

                <div
                  className={`agrl-accept-radio-option ${acceptanceForm.decision === "REJECT" ? "agrl-radio-selected" : ""}`}
                  onClick={() => handleFormChange("decision", "REJECT")}
                  role="presentation"
                >
                  <input
                    type="radio"
                    name="logisticsAgrlAcceptanceDecision"
                    id="logistics-agrl-reject"
                    value="REJECT"
                    checked={acceptanceForm.decision === "REJECT"}
                    onChange={() => handleFormChange("decision", "REJECT")}
                    className="agrl-radio-input"
                  />
                  <label htmlFor="logistics-agrl-reject" className="agrl-radio-label">
                    <div className="agrl-radio-content">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="agrl-radio-icon agrl-icon-red"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <path d="m15 9-6 6" />
                        <path d="m9 9 6 6" />
                      </svg>
                      <div>
                        <p className="agrl-radio-title">Reject GRN</p>
                        <p className="agrl-radio-description">Reject entire shipment and notify supplier</p>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="agrl-accept-approver-section">
              <div className="agrl-accept-approver-grid">
                <div>
                  <label className="agrl-accept-field-label">
                    {acceptanceForm.decision === "REJECT" ? "Rejector Name" : "Approver Name"} <span className="agrl-required">*</span>
                  </label>
                  <input
                    type="text"
                    className="agrl-accept-input"
                    placeholder="Your full name"
                    value={acceptanceForm.approverName}
                    onChange={(ev) => handleFormChange("approverName", ev.target.value)}
                    disabled={submitting}
                    required
                  />
                </div>
                <div>
                  <label className="agrl-accept-field-label">
                    Designation <span className="agrl-required">*</span>
                  </label>
                  <input
                    type="text"
                    className="agrl-accept-input"
                    placeholder="e.g., Warehouse Manager"
                    value={acceptanceForm.designation}
                    onChange={(ev) => handleFormChange("designation", ev.target.value)}
                    disabled={submitting}
                    required
                  />
                </div>
                <div>
                  <label className="agrl-accept-field-label">
                    {acceptanceForm.decision === "REJECT" ? "Rejection Date" : "Approval Date"}
                  </label>
                  <input
                    type="date"
                    className="agrl-accept-input"
                    value={acceptanceForm.approvalDate}
                    onChange={(ev) => handleFormChange("approvalDate", ev.target.value)}
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>

            {acceptanceForm.decision === "APPROVE" ? (
              <div className="agrl-accept-notes-section">
                <label className="agrl-accept-field-label">
                  Approval Notes <span className="agrl-required">*</span>
                </label>
                <textarea
                  className="agrl-accept-textarea"
                  placeholder="Enter approval notes or comments..."
                  rows={3}
                  value={acceptanceForm.approvalNotes}
                  onChange={(ev) => handleFormChange("approvalNotes", ev.target.value)}
                  disabled={submitting}
                  required
                />
              </div>
            ) : null}

            {acceptanceForm.decision === "PARTIALLY_ACCEPT" ? (
              <div className="agrl-accept-followup-section">
                <label className="agrl-accept-field-label">
                  Follow-up Action Required <span className="agrl-required">*</span>
                </label>
                <select
                  className="agrl-accept-select"
                  value={acceptanceForm.followUpAction}
                  onChange={(ev) => handleFormChange("followUpAction", ev.target.value)}
                  disabled={submitting}
                  required
                >
                  {FOLLOW_UP_OPTIONS.map((option) => (
                    <option key={option.value || "empty"} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {acceptanceForm.decision === "REJECT" ? (
              <>
                <div className="agrl-accept-rejection-section">
                  <label className="agrl-accept-field-label">
                    Rejection Reason <span className="agrl-required">*</span>
                  </label>
                  <textarea
                    className="agrl-accept-textarea"
                    placeholder="Explain why this GRN is being rejected..."
                    rows={3}
                    value={acceptanceForm.rejectionReason}
                    onChange={(ev) => handleFormChange("rejectionReason", ev.target.value)}
                    disabled={submitting}
                    required
                  />
                </div>
                <div className="agrl-accept-followup-section">
                  <label className="agrl-accept-field-label">
                    Follow-up Action Required <span className="agrl-required">*</span>
                  </label>
                  <select
                    className="agrl-accept-select"
                    value={acceptanceForm.followUpAction}
                    onChange={(ev) => handleFormChange("followUpAction", ev.target.value)}
                    disabled={submitting}
                    required
                  >
                    {FOLLOW_UP_OPTIONS.map((option) => (
                      <option key={option.value || "empty-reject"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : null}

            <div className="agrl-accept-modal-footer">
              <button type="button" className="agrl-btn agrl-btn-secondary" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="agrl-btn agrl-btn-primary" disabled={submitting}>
                {submitting ? "Submitting…" : "Submit Decision"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
